import React, { useEffect, useState } from 'react';
import { useShouldRemoveAds } from 'lib/subscriptionUtils';
import { GammaVideoPlayer } from 'components/Gamma/VideoPlayer';
import { OmegaVideoPlayer } from 'components/OmegaVideoPlayer';
import { GridStack } from 'components/GridStack';
import { VideoLoadingAnimation } from 'components/OmegaVideoPlayer/LoadingAnimation/Loader';
import { fetchBentoData } from 'lib/bentoUtils';
import { gammaStreamConfigPresets } from 'components/OmegaVideoPlayer/gammaStreamConfigPresets';

/**
 * LinearVideoPlayer - A parent component that delegates between Omega and Gamma video players
 * based on authentication state, feature flags, and stream configuration.
 *
 * This component hoists the conditional logic,
 * @param {object} props
 */
export function LinearVideoPlayer(props) {
  const {
    stream,
    autoplay,
    mutedAutoplay,
    stickyEnabled,
    hasClickedToPlay,
    mvpdHash,
    updateIsVideoPlaying,
    onStuckChange,
    noStuckChangeInFullscreen,
    playmakerArt,
  } = props;

  const shouldRemoveAds = useShouldRemoveAds();

  // Enhanced video data fetching for Bento-based configs
  const [bentoVideoData, setBentoVideoData] = useState(null);
  const [isLoadingBentoVideoData, setIsLoadingBentoVideoData] = useState(false);

  // Get video data from gammaStreamConfigPresets
  /**
   * @type {{ bentoUrl?: string; fallbackVideoData?: any } | null}
   */
  let gammaStreamPreset = null;
  try {
    // Cast to any to avoid TypeScript indexing error in JSX file
    gammaStreamPreset = /** @type {any} */ (gammaStreamConfigPresets)[stream] || null;
  } catch {
    gammaStreamPreset = null;
  }

  // Always call hooks at the top level, not inside conditionals or after returns
  useEffect(() => {
    let ignore = false;
    /**
   * @returns {Promise<void>}
   */
    async function fetchAndSetBentoVideoData() {
      if (gammaStreamPreset?.bentoUrl) {
        setIsLoadingBentoVideoData(true);
        try {
          const data = await fetchBentoData(gammaStreamPreset.bentoUrl);
          // Extract the first asset from data.data.video.videoAssets
          const hasAssets = Array.isArray(data?.data?.video?.videoAssets)
            && data.data.video.videoAssets.length > 0;
          const asset = hasAssets
            ? data.data.video.videoAssets[0]
            : null;
          if (!ignore) {
            // Use Bento data if available, otherwise fall back to preset data
            const mergedData = asset ? {
              ...gammaStreamPreset?.fallbackVideoData,
              ...data.data.video,
              headline: {
                ...gammaStreamPreset?.fallbackVideoData?.headline,
                ...data.data.video?.headline,
              },
              description: {
                ...gammaStreamPreset?.fallbackVideoData?.description,
                ...data.data.video?.description,
              },
              publisher: {
                ...gammaStreamPreset?.fallbackVideoData?.publisher,
                ...data.data.video?.publisher,
              },
            } : null;
            setBentoVideoData(mergedData);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch video data from Bento:', error);
        } finally {
          if (!ignore) setIsLoadingBentoVideoData(false);
        }
      } else {
        setBentoVideoData(null);
      }
    }
    fetchAndSetBentoVideoData();
    return () => { ignore = true; };
  }, [stream, gammaStreamPreset]);

  // Use centralized fallback video data from preset
  const fallbackVideoData = gammaStreamPreset?.fallbackVideoData || null;

  // Use Bento data if available, otherwise fallback to preset data
  // If no preset exists for the stream, the component will render the standard Omega player
  const finalVideoData = bentoVideoData || fallbackVideoData;

  // Determine if we should use Gamma player
  const shouldUseGammaPlayer = shouldRemoveAds;


  if (shouldUseGammaPlayer) {
    // Show loading state while fetching Bento data
    if (gammaStreamPreset?.bentoUrl && isLoadingBentoVideoData) {
      return (
        <GridStack data-testid="bento-loading-container" className="omega-container">
          <VideoLoadingAnimation isReady={false} />
        </GridStack>
      );
    }

    return (
      <GammaVideoPlayer
        video={finalVideoData}
        autoPlay={autoplay}
        onlyAutoplayMuted={mutedAutoplay}
        isEmbedded={false}
        useEmbeddedAds={false}
        hideVideoTitle
      />
    );
  }

  // Default to Omega player for all other cases
  return (
    <OmegaVideoPlayer
      stream={stream}
      autoplay={autoplay}
      mutedAutoplay={mutedAutoplay}
      stickyEnabled={stickyEnabled}
      hasClickedToPlay={hasClickedToPlay}
      mvpdHash={mvpdHash}
      updateIsVideoPlaying={updateIsVideoPlaying}
      onStuckChange={onStuckChange}
      noStuckChangeInFullscreen={noStuckChangeInFullscreen}
      playmakerArt={playmakerArt}
    />
  );
}
